import {
    Availability, AvailabilityFragment,
    AvailabilityShift,
    AvailabilityShiftDefault,
    AvailabilityTimeConstraint, Demand,
    ShiftType
} from '@/graphql/generated/graphql';

import {mutation, query} from "@/graphql/client";
import {gql} from "@urql/vue";
import {useGlobalEmitter} from "@/helper/emitter";
import datetime from "@/helper/datetime/datetime"
import {kapitelDateString, kapitelDateTimeString} from '@/graphql/kapitelTypes';
import {
    clientFragment,
    getShortJobsiteDescription,
    houseFragment,
    jobsiteFragment,
    pluralizedJobsiteAmount, pluralizedJobsiteLabel
} from "./jobsite";
import {getArrayUnique, groupBy} from "@/helper/array";
import {listFormat, pluralize, pluralizeHuman, pluralizeUnitOnly} from "@/helper/amountFormatter";
import {sortShifts, uniqueShifts} from "@/helper/shiftTypes";
import {computed} from "vue";
import {graphql} from "@/graphql/generated";

const emitter = useGlobalEmitter()

const availabilityFragment = graphql(`
    fragment Availability on Availability {
        id
        date
        shifts {
            begin
            until
            type
        }
        constraints {
            begin
            until
            comment
        }
        excludedJobsites {
            ...JobsiteFragment
            client {
                ...ClientFragment
            }
            house {
                ...HouseFragment
            }
        }
        additionalAvailabilityCoordinates {
            begin
            until
            shiftType
            date
            jobsite { ...JobsiteFragment }
        }
    }
`)

const shiftTypeComparator = (typeNeedle: any): { (shift: any): boolean } => (
    (shift: any ) => typeNeedle == shift.type
)

export const isShift = (
    shifts: Array<object>,
    typeNeedle: ShiftType
): boolean => shifts.some(shiftTypeComparator(typeNeedle))

export const getShift = (
    availability: AvailabilityFragment,
    typeNeedle: ShiftType
): AvailabilityShift | undefined => availability.shifts?.find(shiftTypeComparator(typeNeedle))

export function generateAvailability(dateString: kapitelDateString): Availability {
    return <Availability>{
        date: dateString,
        shifts: [],
        constraints: [],
        additionalAvailabilityCoordinates: []
    }
}


export function generatePropertiesFromDefault(dateString: kapitelDateString, shiftDefault: AvailabilityShiftDefault): AvailabilityShift {

    const beginDateTime = datetime.convertDateAndTime2DateTime(
      dateString, shiftDefault.begin
    )

    let untilDateTime = datetime.convertDateAndTime2DateTime(
      dateString, shiftDefault.until
    )

    if(datetime.isAfter(beginDateTime, untilDateTime)){
        untilDateTime = datetime.addDays(untilDateTime, 1)
    }

    return {
        type: shiftDefault.type,
        begin: beginDateTime,
        until: untilDateTime
    }
}

export function generateAvailabilityShift(date: Date, shiftType: ShiftType, begin: kapitelDateTimeString, until: kapitelDateTimeString): AvailabilityShift {
    return <AvailabilityShift>{
        type: shiftType,
        begin: begin,
        until: until
    }
}

export function generateAvailabilityTimeConstraint(begin: kapitelDateTimeString, until: kapitelDateTimeString, comment?: string): AvailabilityTimeConstraint {
    return <AvailabilityTimeConstraint>{
        begin: begin,
        until: until,
        comment
    }
}

export async function fetchAvailabilityForDate(dateString: kapitelDateString) {
    const result = await query(
        graphql(`
            query GetAvailability ($date: KapitelDateImmutable!) {
            availability(
                date: $date
            ) {
                ...Availability
            }
        }`),
        {
            date: dateString
        }
    )

    return result?.data?.availability;
}

export async function fetchAvailabilitiesForDateRange(beginString: kapitelDateString, untilString: kapitelDateString): Promise<any>
{
    const result = await query(
        graphql(`
            query GetAvailabilities (
            $min: KapitelDateImmutable!
            $max: KapitelDateImmutable!
        ) {
            availabilities(betweenDates: {max: $max, min: $min}) {
                ...Availability
            }
        }`), {
            min: beginString,
            max: untilString
        }
    )

    const availabilities = result?.data?.availabilities;
    if (!availabilities) {
        throw new Error("no availabilities in availabilities response")
    }

    return availabilities
}

export async function setAvailability(availability: Availability) {
    useGlobalEmitter().emit('day:mutated', availability.date)

    const MUTATION = graphql(`
            mutation setAvailability(
                $dateDings: KapitelDateImmutable!,
                $shiftsDings: [AvailabilityShiftInput!]!,
                $constraintsDings: [AvailabilityTimeConstraintInput!]!
                $excludedJobsiteIdsDings: [Int!],
                $additionalCoordinatesDings: [AvailabilityCoordinateInput!]
            ) {
                setAvailability(
                    date: $dateDings,
                    shifts: $shiftsDings,
                    constraints: $constraintsDings,
                    excludedJobsiteIds: $excludedJobsiteIdsDings,
                    additionalCoordinates: $additionalCoordinatesDings
                ) {
                    date
                }
            }`)

    const result = await mutation(
        MUTATION, {
            dateDings: availability.date,
            shiftsDings: availability.shifts,
            constraintsDings: availability.constraints,
            excludedJobsiteIdsDings: availability.excludedJobsites ? availability.excludedJobsites.map(j => j.id): undefined,
            additionalCoordinatesDings: availability.additionalAvailabilityCoordinates,
        }
    )

    useGlobalEmitter().emit('day:mutated', availability.date)

    const success = result?.data

    if (!success) {
        throw new Error("error while storing availability")
    }

    return success
}

export function addShift(availability: Availability, shift: AvailabilityShift) {
    availability.shifts.push(shift)
}
export function removeShift(availability: Availability, shift: AvailabilityShift) {
    availability.shifts.splice(availability.shifts.indexOf(shift), 1)
}

export function addConstraint(availability: Availability, constraint: AvailabilityTimeConstraint) {
    availability.constraints.push(constraint)
}
export function removeConstraint(availability: Availability, constraint: AvailabilityTimeConstraint) {
    availability.constraints.splice(availability.constraints.indexOf(constraint), 1)
}


export function availabilityShiftsAndTimeConstraintsToIntervals(shifts: Array<AvailabilityShift>, constraints: Array<AvailabilityTimeConstraint>): Array<{begin: kapitelDateTimeString, until: kapitelDateTimeString}> {
    return datetime.mergeAndDiffIntervals(
      shifts || [],
      constraints || []
    )
}

export function getAvailabilitySummary(availabilities:Availability[]) {

    const shiftTypesList:Array<ShiftType> = []
    availabilities.forEach(availability => {
        availability.shifts.forEach(availabilityShift =>{
            if (!shiftTypesList.includes(availabilityShift.type)) {
                shiftTypesList.push(availabilityShift.type)
            }
        })
        availability.additionalAvailabilityCoordinates.forEach(additionalAvailabilityCoordinate => {
            if (!shiftTypesList.includes(additionalAvailabilityCoordinate.shiftType)) {
                shiftTypesList.push(additionalAvailabilityCoordinate.shiftType)
            }
        })
    })

    const sortedShiftTypesList:ShiftType[]  = uniqueShifts(shiftTypesList).toSorted(sortShifts)

    const shifts = availabilities.reduce((acc: number, availability: Availability) => { return acc += availability.shifts.length }, 0)

    return {
        count: availabilities.filter(a => a.shifts.length > 0).length,
        shifts,
        shiftTypesList:sortedShiftTypesList
    }
}

export function isEmpty(availability: Availability): boolean {
    return (
        !hasShifts(availability)
        && !hasAdditionalAvailabilities(availability)
        && availability.constraints.length == 0
        && (
            !availability.excludedJobsites
            || availability.excludedJobsites?.length == 0
        )
    )
}

export function hasShiftsOrAAs(availability: Availability): boolean {
    return hasShifts(availability)||hasAdditionalAvailabilities(availability)
}

export function hasShifts(availability: Availability): boolean {
    return (
        availability.shifts.length > 0
    )
}

export  function hasAdditionalAvailabilities(availability: Availability): boolean {
    return (
        availability.additionalAvailabilityCoordinates.length > 0
    )
}

export function availabilityEffectiveTimesHumanReadable(availability: Availability) {
    if (!availability) {
        return
    }

    const timeIntervals = availabilityShiftsAndTimeConstraintsToIntervals(availability.shifts, availability.constraints)
    const timespans = timeIntervals.map((interval) =>  datetime.formatKapitelDateTime(interval.begin, datetime.kapitelDateTimeFormatTime)+' - '+datetime.formatKapitelDateTime(interval.until, datetime.kapitelDateTimeFormatTime) + ' Uhr')

    return timespans.join(', ')
}


export const generateAdditionalAvailabilityRenderSet = (availability: Availability, maxEnumarationValues = 2) => {
    const jobsites = getArrayUnique(availability.additionalAvailabilityCoordinates.map(ds => ds.jobsite), (j) => j.id)
    const jobsiteLabel = jobsites.length > maxEnumarationValues ? pluralizeHuman(jobsites.length, 'einer Station', 'Stationen') : listFormat(jobsites.map(js => js?.abbreviation || ''))
    const shifts = uniqueShifts(availability.additionalAvailabilityCoordinates.map(ds => ds.shiftType)).toSorted(sortShifts)
    const shiftLabel = listFormat(shifts)
    const count = availability.additionalAvailabilityCoordinates.length

    return {
        count,
        jobsites,
        jobsiteLabel,
        shifts,
        shiftLabel,
    }
}

export function getAvailabilityLabels(availability: Availability, maxEnumarationValues = 2) {
    const onlyAdditionalAvailabilities = !hasShifts(availability) && hasAdditionalAvailabilities(availability)
    const hasShiftsAndAdditionalAvailabilities = hasShifts(availability) && hasAdditionalAvailabilities(availability)

    const _rs = generateAdditionalAvailabilityRenderSet(availability, maxEnumarationValues)
    const _additionalJS = hasShiftsAndAdditionalAvailabilities ? 'weiteren ' : ''
    const _jobsiteLabel = _rs.jobsites.length > maxEnumarationValues
        ? pluralizeHuman(_rs.jobsites.length, `einer ${_additionalJS}Station`, `${_additionalJS}Stationen`)
        : listFormat(_rs.jobsites.map(js => js?.abbreviation || ''))
    const additionalAvailabilitySummary = _rs.count === 0 ? '' : ((hasShiftsAndAdditionalAvailabilities ? 'und zusätzlich ' : '') + 'auf ' + _jobsiteLabel)


    return {
        onlyAdditionalAvailabilities,
        hasShiftsAndAdditionalAvailabilities,
        hasAdditionalAvailabilities: hasAdditionalAvailabilities(availability),
        label: onlyAdditionalAvailabilities ? 'Verfügbar für offene Dienste' : 'Verfügbar',
        effectiveTimes: availabilityEffectiveTimesHumanReadable(availability),
        additionalAvailabilitySummary,
    }
}
